import { InsurancePlan } from 'src/services/interfaces/insurance';

// Normalized plan used by the Our Plans cards. Values are display-ready strings
// because the API returns benefit details as free-form copay/coinsurance text.
export interface MoneyPair {
  individual: string;
  family: string;
}

export interface LandingPlan {
  id: number;
  deductible: number; // tier (the selector value), parsed from the in-network deductible
  name: string;
  description: string;
  networkType: string;
  primaryCopay: string;
  specialistCopay: string;
  deductibleIn: MoneyPair;
  deductibleOut: MoneyPair;
  oopIn: MoneyPair;
  oopOut: MoneyPair;
  drugGeneric: string;
  drugPreferred: string;
}

export const usd = (n: number) => `$${n.toLocaleString()}`;

// Format a coverage value: pure numbers -> "$1,000", everything else as-is.
const fmtMoney = (v?: string) => {
  if (v == null) return '—';
  const s = String(v).trim();
  if (!s) return '—';
  return /^\d+(\.\d+)?$/.test(s) ? `$${Number(s).toLocaleString()}` : s;
};

const firstLine = (v?: string) =>
  v ? String(v).split('\n')[0].trim() : '—';

type Coverage = Record<string, string>;

// Map the /insurances/plans/full response down to the medical (Cigna PPO) plans
// shown on the Our Plans page.
export function mapMajorMedicalPlans(plans: InsurancePlan[]): LandingPlan[] {
  return plans
    .filter(
      (p) => p.category === 'Major Medical' && !p.archivedAt && p.status === 1
    )
    .map((p) => {
      const c = ((p.details?.coverage as Coverage | undefined) ?? {}) as Coverage;
      const deductible =
        parseInt(String(c['Individual Deductible (In-Network)'] ?? ''), 10) || 0;
      return {
        id: p.id,
        deductible,
        name: p.name,
        description: p.shortDescription || '',
        networkType: p.type || 'PPO',
        primaryCopay: firstLine(c['Primary Care Visit (In-Network)']),
        specialistCopay: firstLine(c['Specialist Visit (In-Network)']),
        deductibleIn: {
          individual: fmtMoney(c['Individual Deductible (In-Network)']),
          family: fmtMoney(c['Family Deductible (In-Network)']),
        },
        deductibleOut: {
          individual: fmtMoney(c['Individual Deductible (Out-of-Network)']),
          family: fmtMoney(c['Family Deductible (Out-of-Network)']),
        },
        oopIn: {
          individual: fmtMoney(c['Individual Max Out-of-Pocket (In-Network)']),
          family: fmtMoney(c['Family Max Out-of-Pocket (In-Network)']),
        },
        oopOut: {
          individual: fmtMoney(c['Individual Max Out-of-Pocket (Out-of-Network)']),
          family: fmtMoney(c['Family Max Out-of-Pocket (Out-of-Network)']),
        },
        drugGeneric: firstLine(c['Generic Drugs (In-Network Pharmacy)']),
        drugPreferred: firstLine(c['Preferred Brand Drugs (In-Network Pharmacy)']),
      };
    })
    .sort((a, b) => a.deductible - b.deductible);
}
